import type { SessionFlowFailure } from '@heidi/runtime/performance';

import { CONTEXT_UPLOAD_FAILURE_TYPES } from './contextUploadTracking';
import { DesktopCaptureFailure } from './desktopCaptureErrors';
import { DOCUMENT_GENERATE_FAILURE_TYPES } from './documentGenerateTracking';
import { DOCUMENT_UPDATE_FAILURE_TYPES } from './documentUpdateTracking';
import { NOTE_GENERATE_FAILURE_TYPES } from './noteGenerateTracking';
import { NOTE_UPDATE_FAILURE_TYPES } from './noteUpdateTracking';
import { RECORDING_FINALIZE_FAILURE_TYPES } from './recordingFinalizeTracking';
import { SESSION_CREATE_FAILURE_TYPES } from './sessionCreateTracking';

// Preflight telemetry only needs the stable error name used by the classifier.
// Keep these app-side errors lightweight; importing Capture's root here would
// initialize the audio engine just to classify an attempt.
export const createSessionFlowPreflightError = (
  name:
    | 'MicPermissionError'
    | 'DeviceInUseError'
    | 'StartRecordingCancelled'
    | 'StartRecordingFailed'
    | 'StopRecordingSuperseded'
    | 'PauseRecordingSuperseded',
  message: string
): Error => {
  const error = new Error(message);
  error.name = name;
  return error;
};

// Audio is live but a later UI or timer step threw. The thrown value can be
// any app error, so the attempt is classified by stage rather than its name.
export const createPostCaptureSetupError = (): Error => {
  const error = new Error('Recording setup failed after capture started');
  error.name = 'PostCaptureSetupFailed';
  return error;
};

/**
 * Maps recording error names to the client session-flow contract's bounded
 * failure vocabulary. Unlisted names collapse to a failed `_OTHER` in the
 * runtime classifier. Names come from the packages/capture error classes and
 * the getUserMedia / getDisplayMedia DOMException names.
 *
 * NotAllowedError covers both a denied microphone and a cancelled tab-share
 * picker; both read as blocked, since the name cannot tell them apart.
 */
export const SESSION_FLOW_FAILURE_TYPES: Readonly<Record<string, SessionFlowFailure>> = {
  NotAllowedError: { outcome: 'blocked', errorType: 'permission_denied' },
  MicPermissionError: { outcome: 'blocked', errorType: 'permission_denied' },
  NotReadableError: { outcome: 'failed', errorType: 'device_in_use' },
  AbortError: { outcome: 'failed', errorType: 'device_in_use' },
  DeviceInUseError: { outcome: 'failed', errorType: 'device_in_use' },
  StartRecordingFailed: { outcome: 'failed', errorType: 'capture_initialization_failed' },
  DexieStartError: { outcome: 'failed', errorType: 'capture_initialization_failed' },
  CreateRecordingIdFailed: {
    outcome: 'failed',
    errorType: 'capture_initialization_failed',
  },
  TabSharingError: { outcome: 'failed', errorType: 'capture_initialization_failed' },
  NoMicDevicesError: { outcome: 'failed', errorType: 'capture_initialization_failed' },
  TooLowStorageError: { outcome: 'failed', errorType: 'capture_initialization_failed' },
  AudioStorageRecoveryFailed: {
    outcome: 'failed',
    errorType: 'capture_initialization_failed',
  },
  // The capture session is held by another browser tab.
  RecordingActiveInAnotherTabError: { outcome: 'failed', errorType: 'device_in_use' },
  StartRecordingCancelled: { outcome: 'cancelled' },
  // An explicit stop retry replaced a stop attempt still open.
  StopRecordingSuperseded: { outcome: 'cancelled' },
  // A pause retry replaced the open attempt.
  PauseRecordingSuperseded: { outcome: 'cancelled' },
  // A logout invalidated the write mid-operation.
  WriteFenceStaleError: { outcome: 'cancelled' },
  // Capture started, then the app reset the recording UI to idle.
  PostCaptureSetupFailed: { outcome: 'failed', errorType: 'post_capture_setup_failed' },

  // Desktop (Tauri). Capture is native, so these come from the recording-start
  // and stop guards rather than from getUserMedia.
  [DesktopCaptureFailure.MIC_PERMISSION_DENIED]: {
    outcome: 'blocked',
    errorType: 'permission_denied',
  },
  [DesktopCaptureFailure.SCREEN_CAPTURE_PERMISSION_DENIED]: {
    outcome: 'blocked',
    errorType: 'permission_denied',
  },
  [DesktopCaptureFailure.NO_AUDIO_DEVICE]: {
    outcome: 'failed',
    errorType: 'capture_initialization_failed',
  },
  [DesktopCaptureFailure.INPUT_DEVICE_DISCONNECTED]: {
    outcome: 'failed',
    errorType: 'capture_initialization_failed',
  },
  [DesktopCaptureFailure.OUTPUT_DEVICE_UNAVAILABLE]: {
    outcome: 'failed',
    errorType: 'capture_initialization_failed',
  },
  [DesktopCaptureFailure.SESSION_NOT_FOUND]: {
    outcome: 'failed',
    errorType: 'capture_initialization_failed',
  },
  [DesktopCaptureFailure.START_STEP_TIMEOUT]: {
    outcome: 'failed',
    errorType: 'capture_initialization_failed',
  },
  [DesktopCaptureFailure.RECORDER_STOP_FAILED]: {
    outcome: 'failed',
    errorType: 'capture_stop_failed',
  },
  // Designed, not a fault: bulk sync recovers the device's audio later. Its own
  // bucket keeps it apart from a recorder that would not stop.
  [DesktopCaptureFailure.REMOTE_DEVICE_DISCONNECTED]: {
    outcome: 'failed',
    errorType: 'device_disconnected',
  },

  // Session create: the names `sessionCreateTracking` raises.
  ...SESSION_CREATE_FAILURE_TYPES,

  // Note generation: the names `noteGenerateTracking` raises.
  ...NOTE_GENERATE_FAILURE_TYPES,

  // Document update: the names `documentUpdateTracking` raises.
  ...DOCUMENT_UPDATE_FAILURE_TYPES,

  // Document generation: the names `documentGenerateTracking` raises.
  ...DOCUMENT_GENERATE_FAILURE_TYPES,
  // Context upload: the names `contextUploadTracking` raises.
  ...CONTEXT_UPLOAD_FAILURE_TYPES,

  // Note update: the names `noteUpdateTracking` raises.
  ...NOTE_UPDATE_FAILURE_TYPES,
  // Recording finalize: the names `recordingFinalizeTracking` raises.
  ...RECORDING_FINALIZE_FAILURE_TYPES,
};

/** The capture engine could not be brought up. */
const FAILED_INIT: SessionFlowFailure = {
  outcome: 'failed',
  errorType: 'capture_initialization_failed',
};

/**
 * A fault the recorder can return from either direction. `operation` already
 * says which one, so the label names the half it broke instead of guessing.
 */
const FAILED_BY_OPERATION: SessionFlowFailure = {
  outcome: 'failed',
  errorTypeByOperation: {
    // The recorder cannot fail a session create.
    session_create: '_OTHER',
    recording_start: 'capture_initialization_failed',
    recording_pause: 'capture_stop_failed',
    recording_resume: 'capture_initialization_failed',
    recording_stop: 'capture_stop_failed',
    // The recorder cannot fail a note or document generation, a note or
    // document save or an upload.
    note_generate: '_OTHER',
    document_update: '_OTHER',
    document_generate: '_OTHER',
    context_upload: '_OTHER',
    note_update: '_OTHER',
    // A finalize classifies its own named errors, never a raw recorder fault.
    recording_finalize: '_OTHER',
  },
};

/**
 * The desktop `error_code` map, keyed off the ONLY Rust path that becomes an
 * `errorCode` on a rejected promise: `impl HasErrorCode for RecordingError`
 * (`src-tauri/src/audio/record/error.rs`), plus the Chronicle bridge's codes.
 *
 * This is not the same set as `packages/plugin-core/src/errors/audio.ts`. That
 * map is the i18n copy table and also covers codes emitted on the `ERROR`
 * window event — `AUDIO_PERMISSION_DENIED`, the whole `AUDIO_MIC_*` /
 * `AUDIO_SPEAKER_*` family — which never reach a rejection and so could never
 * reach `classifySessionFlowFailure`. Keying off it left 18 inert entries here
 * while 8 codes that DO reject a pause or a resume fell through to `_OTHER`.
 *
 * An OS mic denial still classifies, through the FE-raised
 * `DesktopMicPermissionDeniedError` name rather than a code.
 */
export const DESKTOP_SESSION_FLOW_FAILURE_CODES: Readonly<
  Record<string, SessionFlowFailure>
> = {
  // ── Recorder: device and stream bring-up ────────────────────────────────
  AUDIO_SCK_PERMISSION_DENIED: { outcome: 'blocked', errorType: 'permission_denied' },
  AUDIO_NO_INPUT_DEVICE: FAILED_INIT,
  AUDIO_NO_OUTPUT_DEVICE: FAILED_INIT,
  AUDIO_DEVICE_ERROR: FAILED_INIT,
  AUDIO_CONFIG_ERROR: FAILED_INIT,
  AUDIO_STREAM_ERROR: FAILED_INIT,
  AUDIO_STREAM_CREATE_FAILED: FAILED_INIT,
  AUDIO_STREAM_PLAY_FAILED: FAILED_INIT,
  AUDIO_SCK_ERROR: FAILED_INIT,
  RECORDING_START_TIMEOUT: FAILED_INIT,

  // Pausing the stream is the stop half by definition.
  AUDIO_STREAM_PAUSE_FAILED: { outcome: 'failed', errorType: 'capture_stop_failed' },

  // A defensive stop, pause or resume against a recorder that was never
  // started, same no-op shape as `StopRecordingSuperseded`. Never a real
  // failure, so no `errorType` regardless of which operation hit it.
  RECORDING_NOT_STARTED: { outcome: 'cancelled' },

  // ── Recorder: writer, task and internal faults ──────────────────────────
  // Returned by both `start_recording` and `stop_recording`, so the bucket
  // depends on which operation the fault broke rather than on the code.
  RECORDING_INTEGRITY_FAILED: FAILED_BY_OPERATION,
  RECORDING_WRITE_ERROR: FAILED_BY_OPERATION,
  RECORDING_WRITE_TIMEOUT: FAILED_BY_OPERATION,
  RECORDING_WRITER_UNAVAILABLE: FAILED_BY_OPERATION,
  RECORDING_FILE_ERROR: FAILED_BY_OPERATION,
  RECORDING_TASK_PANIC: FAILED_BY_OPERATION,
  RECORDING_TASK_ERROR: FAILED_BY_OPERATION,
  RECORDING_INTERNAL_ERROR: FAILED_BY_OPERATION,

  // ── Heidi Remote (Chronicle), via ChronicleBridgeError.errorCode ────────
  // Produced by tauri-plugin-chronicle's error enum, NOT src-tauri's chronicle
  // module — that one serves bulk download/sync and cannot reject a pause or a
  // resume, so its DEVICE_NOT_CONNECTED / BLE_ERROR codes are absent here.
  CHRONICLE_PERMISSION_DENIED: { outcome: 'blocked', errorType: 'permission_denied' },
  CHRONICLE_DEVICE_NOT_FOUND: FAILED_BY_OPERATION,
  CHRONICLE_CONNECTION_ERROR: FAILED_BY_OPERATION,
  CHRONICLE_BLUETOOTH_ERROR: FAILED_BY_OPERATION,
  CHRONICLE_BLUETOOTH_DISABLED: FAILED_BY_OPERATION,
  CHRONICLE_BLE_WRITE_TIMEOUT: FAILED_BY_OPERATION,
  CHRONICLE_RECONNECT_NEEDED: FAILED_BY_OPERATION,
};
